import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Paperclip, Send, X, Loader2, ListTodo, Save,
  Bold, Italic, Underline, List, ListOrdered, Link2, Quote, Code,
  Users, Flag, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadAttachment } from '../../lib/notesApi';
import { MentionPopover } from './MentionPopover';
import { sanitizeHtml, htmlToText } from './sanitizeHtml';

const _formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const TASK_STATES = [
  { value: 'todo',        label: 'To do',       color: 'bg-slate-100 text-slate-700' },
  { value: 'in_progress', label: 'In progress', color: 'bg-blue-50 text-blue-700' },
  { value: 'blocked',     label: 'Blocked',     color: 'bg-rose-50 text-rose-700' },
  { value: 'done',        label: 'Done',        color: 'bg-emerald-50 text-emerald-700' },
];

const _execCmd = (cmd, value = null) => {
  // document.execCommand is deprecated but still the lightest path for
  // simple rich-text inside contentEditable. Acceptable trade-off for V1.
  try { document.execCommand(cmd, false, value); } catch (e) { /* noop */ }
};

/**
 * Rich-text composer for notes + tasks.
 *
 * Highlights vs the old version:
 * - contentEditable surface preserves bold/italic/links/lists from paste.
 * - "Mark as task" reveals Assignees multi-pick + Task State dropdown.
 * - Mentions (tag-only) stay independent from assignees (ownership).
 * - Optional `is_important` flag with a flag toggle.
 */
export const NoteComposer = ({
  accountId,
  pageKey,
  pageLabel,
  onSubmit,
  initialContent = '',
  initialContentHtml = '',
  initialMentions = [],
  initialAssignees = [],
  initialAttachments = [],
  initialIsTask = false,
  initialTaskState = 'todo',
  initialIsImportant = false,
  parentId = null,
  submitLabel = 'Send',
  editing = false,
  onCancel,
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mentions, setMentions] = useState(initialMentions);
  const [assignees, setAssignees] = useState(initialAssignees);
  const [attachments, setAttachments] = useState(initialAttachments);
  const [isTask, setIsTask] = useState(initialIsTask);
  const [taskState, setTaskState] = useState(initialTaskState || 'todo');
  const [isImportant, setIsImportant] = useState(initialIsImportant);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  // mention popover
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverMode, setPopoverMode] = useState('mention'); // 'mention' | 'assign'
  const [popoverQuery, setPopoverQuery] = useState('');
  const [anchorRect, setAnchorRect] = useState(null);

  const [empty, setEmpty] = useState(true);

  // Seed initial HTML once
  useEffect(() => {
    if (!editorRef.current) return;
    const initial = initialContentHtml || (initialContent ? initialContent.replace(/\n/g, '<br>') : '');
    editorRef.current.innerHTML = initial;
    setEmpty(!htmlToText(editorRef.current.innerHTML).trim());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selection / caret helpers
  const getCaretRect = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(true);
    let rect = range.getClientRects()[0];
    if (!rect) rect = editorRef.current?.getBoundingClientRect();
    return rect ? { top: rect.top, left: rect.left, width: 0, height: rect.height || 20, bottom: rect.bottom, right: rect.right } : null;
  };

  const onInput = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    setEmpty(!htmlToText(html).trim());
    // Detect '@' token at caret
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) { setPopoverOpen(false); return; }
    const anchor = sel.anchorNode;
    const text = (anchor.nodeType === 3 ? anchor.textContent : '') || '';
    const offset = sel.anchorOffset;
    const before = text.slice(0, offset);
    const match = before.match(/@([\w.\- ]{0,40})$/);
    if (match) {
      setPopoverMode('mention');
      setPopoverQuery((match[1] || '').trim());
      setAnchorRect(getCaretRect());
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
  }, []);

  const insertMention = useCallback((u) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = sel.anchorNode;
    if (node && node.nodeType === 3) {
      const text = node.textContent || '';
      const offset = sel.anchorOffset;
      const before = text.slice(0, offset);
      const tokenMatch = before.match(/@([\w.\- ]{0,40})$/);
      const replaceFrom = tokenMatch ? offset - tokenMatch[0].length : offset;
      // Replace the @-token with a styled span
      const after = text.slice(offset);
      node.textContent = text.slice(0, replaceFrom);
      const chip = document.createElement('span');
      chip.className = 'inline-block rounded bg-teal-50 px-1 text-teal-700 font-medium';
      chip.contentEditable = 'false';
      chip.textContent = `@${u.name || u.email}`;
      chip.setAttribute('data-mention-user', u.id);
      const space = document.createTextNode(' ');
      const parent = node.parentNode;
      parent.insertBefore(chip, node.nextSibling);
      parent.insertBefore(space, chip.nextSibling);
      if (after) parent.insertBefore(document.createTextNode(after), space.nextSibling);
      // Move caret after the inserted space
      const newRange = document.createRange();
      newRange.setStartAfter(space);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
    setMentions((prev) => {
      if (prev.some((x) => x.user_id === u.id)) return prev;
      return [...prev, { user_id: u.id, name: u.name || u.email, email: u.email }];
    });
    setPopoverOpen(false);
    onInput();
  }, [onInput]);

  const onPaste = useCallback((e) => {
    e.preventDefault();
    const data = e.clipboardData;
    const html = data.getData('text/html');
    const plain = data.getData('text/plain');
    if (html && html.trim()) {
      const clean = sanitizeHtml(html);
      _execCmd('insertHTML', clean);
    } else if (plain) {
      // Insert as plain text with line-breaks preserved
      _execCmd('insertText', plain);
    }
    onInput();
  }, [onInput]);

  // Toolbar handlers
  const exec = (cmd, value) => {
    editorRef.current?.focus();
    _execCmd(cmd, value);
    onInput();
  };
  const insertLink = () => {
    const sel = window.getSelection();
    const text = sel ? sel.toString() : '';
    const url = window.prompt('Enter URL', text && /^https?:/i.test(text) ? text : 'https://');
    if (!url || !/^https?:/i.test(url)) return;
    editorRef.current?.focus();
    if (sel && !sel.isCollapsed) {
      _execCmd('createLink', url);
    } else {
      _execCmd('insertHTML', `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    }
    onInput();
  };

  // Attachments
  const onFilesPicked = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 20 * 1024 * 1024) { toast.error(`${f.name} is over 20 MB — skipped.`); continue; }
        try { uploaded.push(await uploadAttachment({ accountId, pageKey, file: f })); }
        catch { toast.error(`Couldn't upload ${f.name}`); }
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [accountId, pageKey]);

  const removeMention = (uid) => setMentions((prev) => prev.filter((x) => x.user_id !== uid));
  const removeAssignee = (uid) => setAssignees((prev) => prev.filter((x) => x.user_id !== uid));
  const removeAttachment = (key) => setAttachments((prev) => prev.filter((x) => x.key !== key));

  const canSubmit = useMemo(() => !busy && !uploading && !empty, [busy, uploading, empty]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const rawHtml = editorRef.current?.innerHTML || '';
      const contentHtml = sanitizeHtml(rawHtml);
      const contentText = htmlToText(contentHtml);
      await onSubmit?.({
        account_id: accountId,
        page_key: pageKey,
        page_label: pageLabel,
        content: contentText,
        content_html: contentHtml,
        mentions,
        assignees: isTask ? assignees : [],
        is_task: isTask,
        task_state: isTask ? taskState : null,
        is_important: isImportant,
        parent_id: parentId,
        attachments: attachments.map(({ url: _u, ...rest }) => rest),
      });
      if (!editing) {
        if (editorRef.current) editorRef.current.innerHTML = '';
        setMentions([]);
        setAssignees([]);
        setAttachments([]);
        setIsTask(false);
        setIsImportant(false);
        setTaskState('todo');
        setEmpty(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the note');
    } finally {
      setBusy(false);
    }
  }, [canSubmit, onSubmit, accountId, pageKey, pageLabel, mentions, assignees,
      isTask, taskState, isImportant, attachments, editing, parentId]);

  const onKeyDown = (e) => {
    if (popoverOpen) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // "Assign to" inline picker — re-uses MentionPopover with mode flag
  const openAssignPopover = () => {
    const rect = editorRef.current?.getBoundingClientRect();
    setPopoverMode('assign');
    setPopoverQuery('');
    setAnchorRect(rect);
    setPopoverOpen(true);
  };
  const handlePopoverPick = (u) => {
    if (popoverMode === 'assign') {
      setAssignees((prev) => prev.some((x) => x.user_id === u.id) ? prev : [...prev, { user_id: u.id, name: u.name || u.email, email: u.email }]);
      setPopoverOpen(false);
    } else {
      insertMention(u);
    }
  };

  return (
    <div data-testid="notes-composer" className="border-t border-slate-200 bg-white p-3">
      {/* Mention chips (TAG only) */}
      {mentions.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid="notes-mention-chips">
          {mentions.map((m) => (
            <span key={m.user_id} className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] text-teal-800">
              @{m.name}
              <button onClick={() => removeMention(m.user_id)} className="text-teal-500 hover:text-teal-900" aria-label="Remove tag"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Assignee chips — ONLY when this note is a task */}
      {isTask && assignees.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid="notes-assignee-chips">
          {assignees.map((a) => (
            <span key={a.user_id} className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-800">
              <Users className="h-3 w-3" /> {a.name}
              <button onClick={() => removeAssignee(a.user_id)} className="text-indigo-500 hover:text-indigo-900" aria-label="Remove assignee"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid="notes-attachment-chips">
          {attachments.map((a) => (
            <span key={a.key} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
              <Paperclip className="h-3 w-3 text-slate-500" />
              <span className="max-w-[180px] truncate">{a.name}</span>
              <span className="text-slate-400">{_formatBytes(a.size)}</span>
              <button onClick={() => removeAttachment(a.key)} className="text-slate-400 hover:text-slate-700" aria-label="Remove attachment"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Formatting toolbar */}
      <div className="flex items-center gap-0.5 mb-1 px-0.5 text-slate-500">
        <button type="button" onClick={() => exec('bold')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-bold" title="Bold (⌘B)"><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => exec('italic')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-italic" title="Italic (⌘I)"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => exec('underline')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-underline" title="Underline (⌘U)"><Underline className="h-3.5 w-3.5" /></button>
        <span className="mx-0.5 h-3 w-px bg-slate-200" />
        <button type="button" onClick={() => exec('insertUnorderedList')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-ul" title="Bullet list"><List className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => exec('insertOrderedList')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-ol" title="Numbered list"><ListOrdered className="h-3.5 w-3.5" /></button>
        <span className="mx-0.5 h-3 w-px bg-slate-200" />
        <button type="button" onClick={insertLink} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-link" title="Link"><Link2 className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => exec('formatBlock', '<blockquote>')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-quote" title="Quote"><Quote className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => exec('formatBlock', '<pre>')} className="rounded p-1 hover:bg-slate-100" data-testid="notes-fmt-code" title="Code block"><Code className="h-3.5 w-3.5" /></button>
        <span className="mx-0.5 h-3 w-px bg-slate-200" />
        <button
          type="button"
          onClick={() => setIsImportant((v) => !v)}
          className={'rounded p-1 hover:bg-slate-100 ' + (isImportant ? 'text-amber-600' : '')}
          data-testid="notes-fmt-important"
          title={isImportant ? 'Marked important — click to unmark' : 'Mark as important'}
          aria-pressed={isImportant}
        >
          <Flag className="h-3.5 w-3.5" fill={isImportant ? 'currentColor' : 'none'} />
        </button>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        data-testid="notes-composer-editor"
        data-placeholder="Write a note. Use @ to tag a teammate. Paste retains formatting."
        className="notes-rt min-h-[68px] max-h-[180px] overflow-y-auto w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
      />
      {empty && (
        <p className="-mt-1 px-3 pt-1 text-[11px] text-slate-400 pointer-events-none select-none">
          Write a note. Use @ to tag a teammate. Paste retains formatting.
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileInputRef} type="file" multiple hidden onChange={onFilesPicked} data-testid="notes-attachment-input" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="notes-attachment-btn"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
            Attach
          </button>

          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isTask}
              onChange={(e) => setIsTask(e.target.checked)}
              data-testid="notes-is-task-checkbox"
              className="h-3 w-3 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            <ListTodo className="h-3 w-3" /> Mark as task
          </label>

          {isTask && (
            <>
              <button
                type="button"
                onClick={openAssignPopover}
                data-testid="notes-add-assignee-btn"
                className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-700 hover:bg-indigo-100"
              >
                <Users className="h-3 w-3" /> Assign to
              </button>
              <div className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                <span>Status</span>
                <div className="relative">
                  <select
                    value={taskState}
                    onChange={(e) => setTaskState(e.target.value)}
                    data-testid="notes-task-state-select"
                    className="appearance-none rounded-md border border-slate-200 bg-white px-2 py-1 pr-6 text-[11px] focus:border-teal-500 focus:outline-none"
                  >
                    {TASK_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <ChevronDown className="absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {editing && (
            <button type="button" onClick={onCancel} data-testid="notes-composer-cancel" className="rounded-md px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-100">Cancel</button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="notes-composer-submit"
            className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (editing ? <Save className="h-3 w-3" /> : <Send className="h-3 w-3" />)}
            {submitLabel}
          </button>
        </div>
      </div>

      <MentionPopover
        open={popoverOpen}
        query={popoverQuery}
        accountId={accountId}
        anchorRect={anchorRect}
        onPick={handlePopoverPick}
        onClose={() => setPopoverOpen(false)}
      />
    </div>
  );
};

export default NoteComposer;
